import {
  Box,
  Flex,
  Progress,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Text,
  Tr,
  Button,
  SimpleGrid,
  HStack,
  Spacer,
  useToast,
  useColorModeValue,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import {
  Table as TanStackTable,
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Legend, Pie, PieChart } from 'recharts';

import {
  date as commonDate,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { DeliverableContainerGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { calculateStatus, Status, StatusCell, StatusIcon } from '../Status';
import { getDeliverableDetailViewUrl } from '../utils';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { MultiEntityDrawer as DeliverableEventLogMultiEntityDrawer } from '~/core/routes/deliverables/EntityEventLogDrawers';
import { useCreateDeliverableMutation } from '~/core/routes/deliverables/mutations/CreateDeliverableByDueDate.gql.generated';
import { useDeleteDeliverablesRecordsForContainerMutation } from '~/core/routes/deliverables/mutations/DeleteDeliverablesRecordsForContainer.gql.generated';
import { useGetDeliverableContainersQuery } from '~/core/routes/deliverables/queries/GetDeliverableContainers.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { TanStackPaginationControl } from '~/packages/common-frontend/components/TanStack';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { TaskCenterDrawer } from '~/packages/deliverables/ui/borrower/TaskCenterDrawer';
import {
  getFilterValue,
  ColumnFilter,
  customFullDateStrFilter,
  customMultiFilter,
} from '~/packages/deliverables/ui/DeliverablesColumnFilter';

const MAX_GET_STARTED_DAYS = 30; // Max number of days before a deliverable is due where a user can manually materialize a record.

const computedStatusSortOrder: { [key in Status]: number } = {
  overdue: 6,
  requestRemediation: 5,
  dueSoon: 4,
  notStarted: 3,
  submitted: 2,
  approved: 1,
};

const statusOptions = [
  { label: <StatusCell status="dueSoon" />, value: 'dueSoon' },
  { label: <StatusCell status="notStarted" />, value: 'notStarted' },
  { label: <StatusCell status="overdue" />, value: 'overdue' },
  { label: <StatusCell status="submitted" />, value: 'submitted' },
  {
    label: <StatusCell status="requestRemediation" />,
    value: 'requestRemediation',
  },
  { label: <StatusCell status="approved" />, value: 'approved' },
];

const filterStatuses = (
  table: TanStackTable<DeliverableContainerGQLType>,
  status: string,
) => {
  const column = table.getColumn('calculatedStatus');
  if (column) {
    const currentFilters = getFilterValue(column);
    const index = currentFilters.indexOf(status);
    if (index >= 0) {
      currentFilters.splice(index, 1);
    } else {
      currentFilters.push(status);
    }
    column.setFilterValue(currentFilters);
  }
};

const DeleteDeliverableRecordsButton = ({
  containerId,
  refetchDeliverableContainers,
}: {
  containerId: string;
  refetchDeliverableContainers: Function;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef(null);
  const toast = useToast();

  // THIS SHOULD ONLY BE USED WHEN "isQAUser" IS TRUE
  const [
    deleteDeliverablesRecordsForContainerMutation,
  ] = useDeleteDeliverablesRecordsForContainerMutation({
    onCompleted: () => {
      toast({
        title: 'Successfully deleted records for this deliverable container.',
        duration: 3000,
        status: 'success',
        position: 'top',
      });
    },
    onError: () => {
      toast({
        title: 'Unable to delete records for this deliverable container.',
        status: 'error',
        position: 'top',
      });
    },
  });

  return (
    <>
      <Flex padding="0.5rem">
        <IconButtonV2
          aria-label="Delete"
          icon={<Icon icon="trash" />}
          variant="primary"
          colorScheme="red"
          onClick={onOpen}
        />
      </Flex>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          onClose();
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Deliverable Records
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? You cannot undo this action afterwards.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={async () => {
                  await deleteDeliverablesRecordsForContainerMutation({
                    variables: { containerId },
                  });
                  // await the delete, then you can refetch (this order is important)
                  refetchDeliverableContainers();
                  onClose();
                }}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

type DeliverableContainersTableProps = {
  containers: DeliverableContainerGQLType[];
  refetchDeliverableContainers: Function;
};
const DeliverableContainersTable = ({
  containers,
  refetchDeliverableContainers,
}: DeliverableContainersTableProps) => {
  const { user } = useUserAuthOrThrow();
  const toast = useToast();

  const [createDeliverableMutation] = useCreateDeliverableMutation({
    onError: () => {
      toast({
        title:
          'Unable to create this deliverable container. Please contact Finley support.',
        status: 'error',
        position: 'top',
      });
    },
  });

  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'calculatedStatus', desc: true },
    { id: 'dueDate', desc: false },
  ]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([
    {
      id: 'calculatedStatus',
      value: ['overdue', 'dueSoon', 'requestRemediation', 'notStarted'],
    },
  ]);
  const navigate = useNavigate();

  const statusCounts = useMemo(() => {
    let overdueCount: number = 0;
    let dueSoonCount: number = 0;
    let requestRemediationCount: number = 0;
    let notStartedCount: number = 0;
    let approvedCount: number = 0;
    for (const container of containers) {
      const status = calculateStatus(container);
      if (status === 'dueSoon') {
        dueSoonCount += 1;
      }
      if (status === 'overdue') {
        overdueCount += 1;
      }
      if (status === 'requestRemediation') {
        requestRemediationCount += 1;
      }
      if (status === 'notStarted') {
        notStartedCount += 1;
      }
      if (status === 'approved') {
        approvedCount += 1;
      }
    }

    return {
      overdueCount,
      dueSoonCount,
      requestRemediationCount,
      notStartedCount,
      approvedCount,
    };
  }, [containers]);

  // on refetch, containers doesn't update without useMemo
  const data = useMemo(() => containers, [containers]);

  const statusFilters = useMemo(() => {
    return _.flatMap(
      columnFilters
        .filter((cf) => cf.id === 'calculatedStatus')
        .map((cf) => cf.value),
    );
  }, [columnFilters]);

  const internalQAColumns: ColumnDef<
    DeliverableContainerGQLType,
    any
  >[] = useMemo(
    () => [
      {
        id: 'delete record',
        enableSorting: false,
        enableColumnFilter: false,
        // eslint-disable-next-line react/display-name
        cell: ({ row }) => {
          const { id, totalDependencies, totalFinalArtifacts } = row.original;
          if (totalDependencies === -1 && totalFinalArtifacts === -1) {
            return <></>;
          }
          return (
            <DeleteDeliverableRecordsButton
              containerId={id}
              refetchDeliverableContainers={refetchDeliverableContainers}
            />
          );
        },
        meta: {
          style: {
            padding: '0px',
            textAlign: 'right',
          },
        },
      },
    ],
    [refetchDeliverableContainers],
  );
  const customerColumns: ColumnDef<
    DeliverableContainerGQLType,
    any
  >[] = useMemo(
    () => [
      {
        accessorKey: 'creditFacilityName',
        header: 'Credit Facility',
        enableSorting: false,
        cell: ({ getValue }) => getValue(),
        filterFn: customMultiFilter,
        meta: {
          filterHeader: 'Select Credit Facility/Facilities',
        },
      },
      {
        accessorKey: 'configName',
        header: 'Name',
        enableSorting: false,
        cell: ({ getValue }) => commonUtils.toTitleCase(getValue()),
      },
      {
        id: 'reportType',
        accessorKey: 'type',
        enableSorting: false,
        header: 'Report Type',
        cell: ({ getValue }) => commonUtils.toTitleCase(getValue()),
        filterFn: customMultiFilter,
        meta: {
          filterHeader: 'Select Report Type(s)',
        },
      },
      {
        id: 'dueDate',
        accessorKey: 'dueDate',
        enableSorting: true,
        header: 'Due Date',
        cell: ({ getValue }) =>
          commonDate.fullDateStrToHumanDateString(getValue()),
        filterFn: customFullDateStrFilter,
        meta: {
          filterHeader: 'Select Due Date',
          filterType: 'date',
        },
      },
      {
        id: 'approvedDate',
        enableSorting: true,
        header: 'Approved Date',
        accessorKey: 'approvedDate',
        cell: ({ getValue }) => {
          const approvedDate = getValue();
          return approvedDate
            ? commonDate.fullDateStrToHumanDateString(
                commonDate.epochToFullDateStr(approvedDate),
              )
            : '-';
        },
        filterFn: customFullDateStrFilter,
        meta: {
          filterHeader: 'Select Approved Date',
          filterType: 'date',
        },
      },
      {
        id: 'requiredFiles',
        enableSorting: false,
        enableColumnFilter: false,
        header: 'Required Files',
        size: 275,
        // eslint-disable-next-line react/display-name
        cell: ({ row }) => {
          const {
            totalDependencies,
            completedDependencies,
            dueDate,
            creditFacilityId,
            configKey,
            daysOffset,
          } = row.original;
          const progressValue =
            (completedDependencies / totalDependencies) * 100;
          const notCreatedDeliverable =
            row.original.totalFinalArtifacts === -1 &&
            row.original.totalDependencies === -1;

          const status = calculateStatus(row.original);

          if (notCreatedDeliverable) {
            const currentDate = commonDate.currentDateStr();
            const diff = commonDate.diffDays(dueDate, currentDate);
            if (diff <= MAX_GET_STARTED_DAYS) {
              return (
                <Text fontSize="xs">
                  {status !== 'overdue' &&
                    `We will notify you ${daysOffset} ${
                      daysOffset !== 1 ? 'days' : 'day'
                    } before the deadline.`}{' '}
                  <Button
                    color="blue.400"
                    fontSize="xs"
                    variant="link"
                    onClick={async () => {
                      const {
                        data: newDeliverableData,
                      } = await createDeliverableMutation({
                        variables: {
                          creditFacilityId,
                          dueDate,
                          configKey,
                        },
                      });
                      refetchDeliverableContainers();

                      finleyAssert(
                        newDeliverableData?.deliverable,
                        'Failed to manually create a deliverable from deliverables table.',
                      );
                      const id = newDeliverableData.deliverable.id;
                      navigate(getDeliverableDetailViewUrl(id));
                    }}
                  >
                    Get started
                  </Button>{' '}
                  now if you&apos;d like.
                </Text>
              );
            }
            return <Text>-</Text>;
          }
          return (
            <Stack>
              <Text>{`${completedDependencies}/${totalDependencies}`}</Text>
              <Progress
                value={progressValue}
                colorScheme="green"
                borderRadius={8}
                size="sm"
              />
            </Stack>
          );
        },
      },
      {
        id: 'calculatedStatus',
        accessorFn: (row) => calculateStatus(row),
        enableColumnFilter: true,
        header: 'Status',
        // eslint-disable-next-line react/display-name
        cell: ({ row }) => (
          <StatusCell status={calculateStatus(row.original)} />
        ),
        sortingFn: (rowA, rowB) => {
          const statusRowA = calculateStatus(rowA.original);
          const statusRowB = calculateStatus(rowB.original);
          if (
            computedStatusSortOrder[statusRowA] >
            computedStatusSortOrder[statusRowB]
          ) {
            return 1;
          }
          if (
            computedStatusSortOrder[statusRowA] <
            computedStatusSortOrder[statusRowB]
          ) {
            return -1;
          }
          return 0;
        },
        filterFn: customMultiFilter,
        meta: {
          filterHeader: 'Select Status(es)',
          filterOptions: statusOptions,
        },
      },
      {
        id: 'moreDetail',
        enableSorting: false,
        enableColumnFilter: false,
        // eslint-disable-next-line react/display-name
        cell: ({ row }) => {
          const { id, totalDependencies, totalFinalArtifacts } = row.original;
          if (totalDependencies === -1 && totalFinalArtifacts === -1) {
            return <></>;
          }
          return (
            <Flex padding="0.5rem">
              <IconButtonV2
                aria-label="More details"
                icon={<Icon icon="chevron-right" />}
                variant="v2-ghost"
                onClick={() => navigate(`/deliverables/${id}`)}
              />
            </Flex>
          );
        },
        meta: {
          style: {
            padding: '0px',
            textAlign: 'right',
          },
        },
      },
    ],
    [createDeliverableMutation, navigate, refetchDeliverableContainers],
  );

  const isQAUser =
    user.email.startsWith('test') && user.email.endsWith('@finleycms.com');
  const columns = isQAUser
    ? [...internalQAColumns, ...customerColumns]
    : customerColumns;

  const table = useReactTable({
    data,
    columns,
    enableMultiSort: true,
    state: {
      sorting,
      columnFilters,
    },
    initialState: {
      sorting,
      columnFilters,
      pagination: {
        pageIndex: 0,
        pageSize: 35,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
    excludeColumnIds: ['delete record', 'moreDetail'],
  });

  const pieChartColor = useColorModeValue(colors.black[70], 'white');
  const tableBorderColor = useColorModeValue(colors.gray[100], 'white');
  const tableHeaderColor = useColorModeValue(colors.black[95], 'white');
  const columnSortColor = useColorModeValue(colors.aurora[400], 'white');
  const tableBodyColor = useColorModeValue('gray.600', 'white');

  const filterCardSelectedBorderColor = useColorModeValue('black', 'white');
  const filterCardBorderColor = useColorModeValue('gray.100', 'gray.800');

  return (
    <Box>
      <HStack width="full" spacing="20px">
        {/* Pie Chart! */}
        <PieChart width={550} height={400}>
          <Pie
            width={160}
            height={160}
            data={[
              {
                name: `Overdue`,
                value: statusCounts.overdueCount,
                fill: colors.red[200],
              },
              {
                name: `Remediation Requested`,
                value: statusCounts.requestRemediationCount,
                fill: colors.orange[300],
              },
              {
                name: `Due Soon`,
                value: statusCounts.dueSoonCount,
                fill: colors.orange[200],
              },
              {
                name: `Not Started`,
                value: statusCounts.notStartedCount,
                fill: colors.blue[300],
              },
              {
                name: `Approved`,
                value: statusCounts.approvedCount,
                fill: colors.green[200],
              },
            ]}
            dataKey="value"
            nameKey="name"
          />
          <Legend
            verticalAlign="middle"
            align="right"
            layout="vertical"
            iconType="circle"
            iconSize={8}
            formatter={(value) => (
              <Text color={pieChartColor} display="inline-block" ml="8px">
                {value}
              </Text>
            )}
          />
        </PieChart>

        {/* Hot Buttons / Filter Cards */}
        <SimpleGrid width="full" columns={1} spacing={4}>
          <Button
            _focus={{ boxShadow: 'none' }}
            leftIcon={<StatusIcon status="overdue" />}
            whiteSpace="normal"
            py={6}
            variant="outline"
            borderColor={
              statusFilters.includes('overdue')
                ? filterCardSelectedBorderColor
                : filterCardBorderColor
            }
            onClick={() => {
              filterStatuses(table, 'overdue');
            }}
          >
            <HStack width="full">
              <Text>Overdue</Text>
              <Spacer />
              <Text>{statusCounts.overdueCount}</Text>
            </HStack>
          </Button>
          <Button
            _focus={{ boxShadow: 'none' }}
            leftIcon={<StatusIcon status="requestRemediation" />}
            whiteSpace="normal"
            py={6}
            variant="outline"
            borderColor={
              statusFilters.includes('requestRemediation')
                ? filterCardSelectedBorderColor
                : filterCardBorderColor
            }
            onClick={() => {
              filterStatuses(table, 'requestRemediation');
            }}
          >
            <HStack width="full">
              <Text>Remediation Requested</Text>
              <Spacer />
              <Text>{statusCounts.requestRemediationCount}</Text>
            </HStack>
          </Button>
          <Button
            _focus={{ boxShadow: 'none' }}
            leftIcon={<StatusIcon status="dueSoon" />}
            whiteSpace="normal"
            py={6}
            variant="outline"
            borderColor={
              statusFilters.includes('dueSoon')
                ? filterCardSelectedBorderColor
                : filterCardBorderColor
            }
            onClick={() => {
              filterStatuses(table, 'dueSoon');
            }}
          >
            <HStack width="full">
              <Text>Due Soon</Text>
              <Spacer />
              <Text>{statusCounts.dueSoonCount}</Text>
            </HStack>
          </Button>
          <Button
            _focus={{ boxShadow: 'none' }}
            leftIcon={<StatusIcon status="notStarted" />}
            whiteSpace="normal"
            py={6}
            variant="outline"
            borderColor={
              statusFilters.includes('notStarted')
                ? filterCardSelectedBorderColor
                : filterCardBorderColor
            }
            onClick={() => {
              filterStatuses(table, 'notStarted');
            }}
          >
            <HStack width="full">
              <Text>Not Started</Text>
              <Text color="gray.500" fontWeight="normal">
                due in the next 30 days
              </Text>
              <Spacer />
              <Text>{statusCounts.notStartedCount}</Text>
            </HStack>
          </Button>
          <Button
            _focus={{ boxShadow: 'none' }}
            leftIcon={<StatusIcon status="approved" />}
            whiteSpace="normal"
            py={6}
            variant="outline"
            borderColor={
              statusFilters.includes('approved')
                ? filterCardSelectedBorderColor
                : filterCardBorderColor
            }
            onClick={() => {
              filterStatuses(table, 'approved');
            }}
          >
            <HStack width="full">
              <Text>Approved</Text>
              <Text color="gray.500" fontWeight="normal">
                past 12 months
              </Text>
              <Spacer />
              <Text>{statusCounts.approvedCount}</Text>
            </HStack>
          </Button>
        </SimpleGrid>
      </HStack>
      <Box mb="1.5em">
        <Flex justify="end">
          <CsvXlsxDownloadSelect
            dataRows={csvDownload.dataRows}
            headers={csvDownload.headers}
            filenamePrefix="deliverables"
          />
        </Flex>
      </Box>
      <Table borderWidth="1px" borderColor={tableBorderColor}>
        <Thead>
          {_.map(table.getHeaderGroups(), (headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const columnCanSort = header.column.getCanSort();
                const columnCanFilter = header.column.getCanFilter();

                return (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    textTransform="capitalize"
                    padding={
                      header.column.columnDef.meta?.style?.padding ||
                      '8px 16px 8px 16px'
                    }
                    fontSize="16px"
                    fontWeight="medium"
                    whiteSpace="nowrap"
                    color={tableHeaderColor}
                    width={
                      header.id === 'requiredFiles' ? header.getSize() : 'auto'
                    }
                  >
                    <Flex alignItems="center">
                      <Box
                        cursor={columnCanSort ? 'pointer' : 'default'}
                        onClick={header.column.getToggleSortingHandler()}
                        mr="8px"
                        color={columnSortColor}
                        fontWeight="thin"
                      >
                        {{
                          asc: <Icon icon="sort-up" />,
                          desc: <Icon icon="sort-down" />,
                        }[header.column.getIsSorted() as string] ??
                          (columnCanSort ? <Icon icon="sort" /> : null)}
                      </Box>
                      <Box>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Box>
                      {columnCanFilter && (
                        <ColumnFilter
                          key={JSON.stringify(getFilterValue(header.column))}
                          type={header.column.columnDef.meta?.filterType}
                          header={header.column.columnDef.meta?.filterHeader}
                          options={header.column.columnDef.meta?.filterOptions}
                          column={header.column}
                          valueFormatFn={
                            header.column.columnDef.meta?.filterFormatFn
                          }
                        />
                      )}
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody color={tableBodyColor} fontSize="sm">
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  textAlign={cell.column.columnDef.meta?.style?.textAlign}
                  padding={cell.column.columnDef.meta?.style?.padding}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
      <TanStackPaginationControl table={table} />
    </Box>
  );
};

const RouteBase = () => {
  /* 🟢 Borrower/Lender same data */
  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = useMemo(
    () => facilities.map((facility) => facility.id),
    [facilities],
  );

  /* 🔴 Borrower/Lender different data */
  const deliverableContainersQuery = useGetDeliverableContainersQuery({
    variables: {
      creditFacilityIds,
      limit: 100,
    },
  });

  if (
    !deliverableContainersQuery.data?.getDeliverableContainers ||
    deliverableContainersQuery.loading
  ) {
    return <Skeleton height="600px" />;
  }

  return (
    <>
      <Flex justify="space-between" gap="1rem">
        <Typography variant="page-title" trim>
          Deliverables
        </Typography>
        <Flex gap="1rem">
          <DeliverableEventLogMultiEntityDrawer />
          {/* 🔴 Borrower/Lender different data */}
          <TaskCenterDrawer />
        </Flex>
      </Flex>

      {/* 🔴 Borrower/Lender different data */}
      {deliverableContainersQuery.data.getDeliverableContainers.length === 0 ? (
        <Text>No deliverables are currently configured.</Text>
      ) : (
        <DeliverableContainersTable
          containers={deliverableContainersQuery.data.getDeliverableContainers}
          refetchDeliverableContainers={deliverableContainersQuery.refetch}
        />
      )}
    </>
  );
};

// Exports
export const Route = () => <RouteBase />;
